import { supabase } from '../lib/supabase';
import productionService from './productionService';

/** URL absolue pointant vers la vue atelier (scan sans liste préalable). */
export function buildWorkshopLotScanUrl(batchId) {
  if (!batchId || typeof window === 'undefined') return '';
  const base = import.meta.env.BASE_URL || '/';
  const normalized = base.endsWith('/') ? base : `${base}/`;
  const path = `${normalized}atelier-lot?batch=${encodeURIComponent(batchId)}`;
  return new URL(path, window.location.origin).toString();
}

/**
 * Valeur QR pour les fiches (production / IPR) : scannable depuis l’atelier.
 * Priorité : URL atelier (?batch=) → UUID lot → UUID cas → n° lot → n° cas.
 */
export function buildSheetWorkshopQrValue({
  batchId = null,
  caseId = null,
  batchNumber = null,
  caseNumber = null,
} = {}) {
  const usable = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    if (!s || s === '—') return null;
    return s;
  };
  const bid = usable(batchId);
  if (bid) {
    const url = typeof window !== 'undefined' ? buildWorkshopLotScanUrl(bid) : '';
    if (url) return url;
    return bid;
  }
  const cid = usable(caseId);
  if (cid) return cid;
  const bnum = usable(batchNumber);
  if (bnum) return bnum;
  const cnum = usable(caseNumber);
  if (cnum) return cnum;
  return '';
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Interprète un JSON de fiche (anciens QR PRODUCTION_SHEET / IPR_STRIPPING_SHEET).
 */
function parseSheetJsonPayload(trimmed) {
  if (!trimmed.startsWith('{')) return null;
  try {
    const obj = JSON.parse(trimmed);
    if (!obj || typeof obj !== 'object') return null;
    const batchId = obj.batchId || obj.batch_id || null;
    if (batchId && UUID_RE.test(String(batchId))) {
      return { type: 'uuid', id: String(batchId) };
    }
    const batchNumber = obj.batchNumber || obj.batch_number || null;
    if (batchNumber && String(batchNumber).trim()) {
      return { type: 'batchNumber', batchNumber: String(batchNumber).trim() };
    }
    const caseId = obj.caseId || obj.case_id || null;
    if (caseId && UUID_RE.test(String(caseId))) {
      return { type: 'uuid', id: String(caseId) };
    }
    const caseNumber = obj.caseNumber || obj.case_number || null;
    if (caseNumber && String(caseNumber).trim()) {
      return { type: 'unknown', raw: String(caseNumber).trim() };
    }
  } catch {
    // not JSON
  }
  return null;
}

/**
 * Interprète le contenu d’un QR ou d’une saisie : URL ?batch=, UUID seul, LOT-xxx-timestamp, etc.
 */
export function parseLotScanPayload(raw) {
  if (raw == null || String(raw).trim() === '') return { type: 'empty' };
  const trimmed = String(raw).trim();

  const fromSheetJson = parseSheetJsonPayload(trimmed);
  if (fromSheetJson) return fromSheetJson;

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const path = trimmed.startsWith('http')
      ? trimmed
      : trimmed.startsWith('/')
        ? `${origin}${trimmed}`
        : `${origin}/${trimmed.replace(/^\//, '')}`;
    const u = new URL(path);
    const batch = u.searchParams.get('batch');
    if (batch && UUID_RE.test(batch)) return { type: 'uuid', id: batch };
    if (batch) return { type: 'opaqueId', id: batch };
  } catch {
    // not a URL
  }

  if (UUID_RE.test(trimmed)) return { type: 'uuid', id: trimmed };

  // Format actuel (generateLotBatchNumber, fiche lot) : LOT-YYYYMMDD-SUFFIX — stocké tel quel dans batch_number
  if (/^LOT-\d{8}-[A-Za-z0-9]+$/i.test(trimmed)) {
    return { type: 'batchNumber', batchNumber: trimmed };
  }
  if (/^BATCH-\d{8}-[A-Za-z0-9]+$/i.test(trimmed)) {
    return { type: 'batchNumber', batchNumber: trimmed };
  }

  // Ancien QR / libellés : LOT-<identifiant>-<timestamp 10+ chiffres> → la partie centrale était parfois seule en base
  const legacyLot = trimmed.match(/^LOT-(.+)-(\d{10,})$/i);
  if (legacyLot) return { type: 'batchNumber', batchNumber: legacyLot[1].trim() };
  const legacyBatch = trimmed.match(/^BATCH-(.+)-(\d{10,})$/i);
  if (legacyBatch) return { type: 'batchNumber', batchNumber: legacyBatch[1].trim() };

  if (/^LOT-/i.test(trimmed)) {
    const rest = trimmed.replace(/^LOT-/i, '');
    const stripped = rest.replace(/-\d{10,}$/, '').trim();
    // Corps DATE-SUFFIX sans préfixe → en base c’est souvent LOT-DATE-SUFFIX
    if (/^\d{8}-[A-Za-z0-9]+$/i.test(stripped)) {
      return { type: 'batchNumber', batchNumber: `LOT-${stripped}` };
    }
    return { type: 'batchNumber', batchNumber: stripped || trimmed };
  }
  if (/^BATCH-/i.test(trimmed)) {
    const rest = trimmed.replace(/^BATCH-/i, '');
    const stripped = rest.replace(/-\d{10,}$/, '').trim();
    if (/^\d{8}-[A-Za-z0-9]+$/i.test(stripped)) {
      return { type: 'batchNumber', batchNumber: `BATCH-${stripped}` };
    }
    return { type: 'batchNumber', batchNumber: stripped || trimmed };
  }

  // Saisie manuelle « 20260412-QRK3W » sans préfixe
  if (/^\d{8}-[A-Za-z0-9]+$/i.test(trimmed)) {
    return { type: 'batchNumber', batchNumber: `LOT-${trimmed}` };
  }

  return { type: 'unknown', raw: trimmed };
}

/**
 * Charge le lot à partir du résultat de `parseLotScanPayload`.
 */
export async function resolveBatchFromScan(parsed) {
  if (!parsed || parsed.type === 'empty') return { batch: null, error: null };
  if (parsed.type === 'uuid' || parsed.type === 'opaqueId') {
    try {
      const batch = await productionService.getById(parsed.id);
      return { batch: batch?.id ? batch : null, error: batch?.id ? null : 'Lot introuvable' };
    } catch (e) {
      return { batch: null, error: e.message || 'Lot introuvable' };
    }
  }
  if (parsed.type === 'batchNumber' && parsed.batchNumber) {
    const { data, error } = await supabase
      .from('production_batches')
      .select('id')
      .eq('batch_number', parsed.batchNumber)
      .maybeSingle();
    if (error) return { batch: null, error: error.message };
    if (!data?.id) return { batch: null, error: 'Lot introuvable' };
    try {
      const batch = await productionService.getById(data.id);
      return { batch, error: null };
    } catch (e) {
      return { batch: null, error: e.message || 'Lot introuvable' };
    }
  }
  return { batch: null, error: 'Code non reconnu' };
}

const TERMINAL_BATCH_STATUSES = new Set(['completed', 'cancelled']);

/**
 * Lots du cas, lot « actif » = premier non terminé par updated_at décroissant ; sinon dernier lot (info).
 */
export async function fetchActiveBatchForCaseId(caseId) {
  if (!caseId) return { batch: null, error: null, info: undefined };
  const { data: caseRow, error: caseErr } = await supabase
    .from('cases')
    .select('id')
    .eq('id', caseId)
    .maybeSingle();
  if (caseErr) return { batch: null, error: caseErr.message, info: undefined };
  if (!caseRow?.id) return { batch: null, error: 'Cas introuvable', info: undefined };

  const { data: rows, error } = await supabase
    .from('production_batches')
    .select('id, status, updated_at')
    .eq('case_id', caseId)
    .order('updated_at', { ascending: false, nullsFirst: false })
    .limit(40);
  if (error) return { batch: null, error: error.message, info: undefined };
  const list = rows || [];
  if (list.length === 0) return { batch: null, error: 'Aucun lot pour ce cas', info: undefined };
  const open = list.find((r) => !TERMINAL_BATCH_STATUSES.has(String(r.status || '')));
  const chosen = open || list[0];
  try {
    const batch = await productionService.getById(chosen.id);
    if (!batch?.id) return { batch: null, error: 'Lot introuvable', info: undefined };
    const info = open ? undefined : 'Dernier lot affiché (aucun lot actif pour ce cas).';
    return { batch, error: null, info };
  } catch (e) {
    return { batch: null, error: e.message || 'Lot introuvable', info: undefined };
  }
}

export async function fetchActiveBatchForCaseNumber(caseNumber) {
  const trimmed = String(caseNumber ?? '').trim();
  if (!trimmed) return { batch: null, error: null, info: undefined };
  const { data: caseRow, error } = await supabase
    .from('cases')
    .select('id')
    .eq('case_number', trimmed)
    .maybeSingle();
  if (error) return { batch: null, error: error.message, info: undefined };
  if (!caseRow?.id) return { batch: null, error: 'Cas introuvable', info: undefined };
  return fetchActiveBatchForCaseId(caseRow.id);
}

/**
 * Résout lot puis, en repli, cas (UUID = id cas si lot absent ; type unknown = numéro de cas).
 */
export async function resolveWorkshopEntryFromRaw(raw) {
  const parsed = parseLotScanPayload(raw);
  if (parsed.type === 'empty') return { batch: null, error: null, info: undefined };

  const lotFirst = await resolveBatchFromScan(parsed);
  if (lotFirst.batch) return { batch: lotFirst.batch, error: null, info: undefined };

  if (parsed.type === 'uuid' && parsed.id) {
    const viaCase = await fetchActiveBatchForCaseId(parsed.id);
    if (viaCase.batch) return viaCase;
  }

  if (parsed.type === 'unknown' && parsed.raw) {
    const viaNum = await fetchActiveBatchForCaseNumber(parsed.raw);
    if (viaNum.batch) return viaNum;
    return {
      batch: null,
      error: viaNum.error || lotFirst.error || 'Lot ou cas introuvable',
      info: undefined,
    };
  }

  return { batch: null, error: lotFirst.error || 'Lot introuvable', info: undefined };
}
